import { useState } from 'react';

import { ErrorApi } from '@haus/core';

import { Boton } from '../componentes/Boton.js';
import { EntradaPin } from '../componentes/Pin.js';
import { Aviso, BarraAccion, Cabecera, Contenido, Pantalla } from '../componentes/estructura.js';
import { useApp } from '../estado/app.js';
import css from './comun.module.css';

/**
 * 01 · Acceso con PIN.
 *
 * Ojo con lo que hace y lo que no: **el PIN no es la credencial**. Lo que
 * autentica es el token de dispositivo que se guardó al canjear la invitación;
 * el PIN solo desbloquea este móvil. Por eso aquí no hay usuario ni contraseña
 * y por eso perder el PIN no da acceso a nadie que no tenga ya el teléfono.
 *
 * A los cinco fallos el móvil se bloquea y solo oficina lo desbloquea. Es
 * duro a propósito: seis dígitos son un millón de combinaciones, que en
 * criptografía no es nada, y lo que lo hace aceptable es justamente que no se
 * pueda insistir.
 */
export function PantallaAcceso() {
    const { t, cliente, guardar, ir, sesion, cola } = useApp();
    const [error, setError] = useState<string | null>(null);
    const [bloqueado, setBloqueado] = useState(false);
    // Oficina le ha borrado el PIN y hay que ponerle uno nuevo. `null` = no
    // toca; `''` = eligiendo el primero; con seis digitos = repitiendolo.
    const [pinNuevo, setPinNuevo] = useState<string | null>(null);

    if (sesion === null) {
        // Sin token de dispositivo no hay nada que desbloquear: hay que volver
        // a darse de alta con una invitación nueva.
        return (
            <Pantalla>
                <Contenido>
                    <div className={css.centro}>
                        <span className={css.logo}>H</span>
                        <h1 className={css.titulo}>{t('alta.sin_token')}</h1>
                        <p className={css.parrafo}>{t('alta.sin_token_ayuda')}</p>
                    </div>
                </Contenido>
            </Pantalla>
        );
    }

    async function entrar(pin: string): Promise<void> {
        setError(null);
        try {
            const acceso = await cliente.acceder(sesion!.device_token, pin);
            await guardar({
                ...sesion!,
                jwt: acceso.jwt,
                expira_at: Date.now() + acceso.expira_en * 1000,
                empleada: acceso.employee,
            });
            void cola.arrancar('acceso');
            ir({ n: 'hoy' });
        } catch (e) {
            if (!(e instanceof ErrorApi)) {
                setError(t('error.inesperado'));

                return;
            }

            if (e.codigo === 'pin_incorrecto') {
                const restantes = e.extra['intentos_restantes'];
                setError(
                    typeof restantes === 'number'
                        ? t('pin.error_intentos', { intentos: restantes })
                        : t('pin.error')
                );

                return;
            }

            if (e.codigo === 'dispositivo_bloqueado' || e.codigo === 'pin_bloqueado') {
                setBloqueado(true);

                return;
            }

            // Oficina lo ha restablecido: no hay PIN que meter, hay uno que
            // elegir. El token del dispositivo sigue valiendo, asi que no
            // hace falta invitacion ni reinstalar la app.
            if (e.codigo === 'pin_no_creado') {
                setPinNuevo('');

                return;
            }

            if (e.dispositivoInutil) {
                setError(t('error.dispositivo_revocado'));

                return;
            }

            setError(e.codigo === 'sin_red' ? t('error.sin_red') : t('error.inesperado'));
        }
    }

    async function ponerPin(pin: string): Promise<void> {
        setError(null);
        try {
            const acceso = await cliente.crearPin(sesion!.device_token, pin);
            await guardar({
                ...sesion!,
                jwt: acceso.jwt,
                expira_at: Date.now() + acceso.expira_en * 1000,
                empleada: acceso.employee,
            });
            void cola.arrancar('acceso');
            setPinNuevo(null);
            ir({ n: 'hoy' });
        } catch (e) {
            if (!(e instanceof ErrorApi)) {
                setError(t('error.inesperado'));
                setPinNuevo('');

                return;
            }

            // Oficina se ha arrepentido, o ya lo puso desde otro movil: el PIN
            // vuelve a existir, asi que hay que volver a pedirlo, no crearlo.
            if (e.codigo === 'pin_ya_creado') {
                setPinNuevo(null);
                setError(t('pin.reiniciado_ya'));

                return;
            }

            setError(
                e.codigo === 'pin_debil'
                    ? t('pin.debil_iguales')
                    : e.codigo === 'pin_formato'
                      ? t('pin.debil_iguales')
                      : e.codigo === 'sin_red'
                        ? t('error.sin_red')
                        : t('error.inesperado')
            );
            setPinNuevo('');
        }
    }

    if (pinNuevo !== null) {
        const repitiendo = pinNuevo.length === 6;

        return (
            <Pantalla>
                <Cabecera
                    titulo={repitiendo ? t('pin.repetir_titulo') : t('pin.reiniciado_titulo')}
                    subtitulo={repitiendo ? undefined : t('pin.reiniciado_intro')}
                    alVolver={
                        repitiendo
                            ? () => {
                                  setError(null);
                                  setPinNuevo('');
                              }
                            : undefined
                    }
                />
                <Contenido>
                    <EntradaPin
                        error={error}
                        etiquetaBorrar={t('pin.borrar')}
                        onCompleto={(pin) => {
                            if (!repitiendo) {
                                setError(null);
                                setPinNuevo(pin);

                                return;
                            }
                            if (pin !== pinNuevo) {
                                setError(t('pin.no_coincide'));
                                setPinNuevo('');

                                return;
                            }
                            void ponerPin(pin);
                        }}
                    />
                </Contenido>
            </Pantalla>
        );
    }

    if (bloqueado) {
        return (
            <Pantalla>
                <Contenido>
                    <div className={css.centro}>
                        <span className={css.logo}>H</span>
                        <h1 className={css.titulo}>{t('pin.bloqueado')}</h1>
                        <p className={css.parrafo}>{t('pin.bloqueado_ayuda')}</p>
                    </div>
                </Contenido>
                <BarraAccion>
                    <Boton variante="secundario" onClick={() => ir({ n: 'cola' })}>
                        {t('cola.titulo')}
                    </Boton>
                </BarraAccion>
            </Pantalla>
        );
    }

    return (
        <Pantalla>
            <Contenido>
                <span className={css.logo}>H</span>
                <h1 className={`${css.titulo} ${css.tituloXl}`}>{t('pin.saludo')}</h1>
                <p className={css.parrafo}>
                    {t('pin.subtitulo', {
                        empleada: sesion.empleada.nombre,
                        dispositivo: sesion.etiqueta_dispositivo,
                    })}
                </p>

                <EntradaPin error={error} etiquetaBorrar={t('pin.borrar')} onCompleto={(p) => void entrar(p)} />

                <Aviso tono="info">{t('pin.olvidado_ayuda')}</Aviso>
            </Contenido>
        </Pantalla>
    );
}
